/**
 * 钱包服务
 * 职责：封装钱包相关的API请求，包括钱包信息、充值、消费记录等
 *
 * 统一调用规范：
 * - 所有接口通过 publish 后端代理调用，由后端通过 SDK 访问 shared 服务
 * - 统一使用 api.js (axios实例)，不再直接调用 shared-service
 * - 保证调用链路：前端 -> publish后端 -> auth-sdk -> shared服务
 *
 * 依赖：api.js (axios实例)
 */
import api from './api';

// 本服务使用的 axios 实例（统一使用 api.js）
// 移除了直接调用 shared-service 的 walletApi 实例

const walletService = {
  // ==================== 用户钱包相关 ====================

  /**
   * 获取钱包信息
   * GET /api/pubs/v1/wallet/info
   * 代理路径：publish后端 -> auth-sdk -> shared服务
   */
  getWalletInfo: () => {
    return api.get('/api/pubs/v1/wallet/info');
  },

  /**
   * 获取钱包统计
   * GET /api/pubs/v1/wallet/statistics
   */
  getWalletStatistics: () => {
    return api.get('/api/pubs/v1/wallet/statistics');
  },

  /**
   * 获取金蝉值明细（分页）
   * GET /api/pubs/v1/wallet/point-details
   */
  getPointDetails: (params) => {
    return api.get('/api/pubs/v1/wallet/point-details', { params });
  },

  /**
   * 获取即将过期的金蝉值
   * GET /api/pubs/v1/wallet/expiring-soon
   */
  getExpiringSoonPoints: (days = 30) => {
    return api.get('/api/pubs/v1/wallet/expiring-soon', { params: { days } });
  },

  // ==================== 充值相关 ====================

  /**
   * 获取充值套餐列表
   * GET /api/pubs/v1/recharge/packages
   */
  getRechargePackages: () => {
    return api.get('/api/pubs/v1/recharge/packages');
  },

  /**
   * 创建充值订单
   * POST /api/pubs/v1/recharge/create-order
   * @param {Object} data - 请求数据
   * @param {number} data.packageId - 套餐ID
   * @param {string} data.paymentMethod - 支付方式（WECHAT_NATIVE, WECHAT_H5, ALIPAY）
   */
  createRechargeOrder: (data) => {
    return api.post('/api/pubs/v1/recharge/create-order', data);
  },

  /**
   * 获取用户订单列表（分页）
   * GET /api/pubs/v1/recharge/orders
   */
  getRechargeOrders: (params) => {
    return api.get('/api/pubs/v1/recharge/orders', { params });
  },

  /**
   * 获取订单详情
   * GET /api/pubs/v1/recharge/order/{orderNo}
   */
  getRechargeOrderDetail: (orderNo) => {
    return api.get(`/api/pubs/v1/recharge/order/${orderNo}`);
  },

  /**
   * 获取订单详情（别名，用于查询订单状态）
   * GET /api/pubs/v1/recharge/order/{orderNo}
   */
  getRechargeOrderByNo: (orderNo) => {
    return api.get(`/api/pubs/v1/recharge/order/${orderNo}`);
  },

  /**
   * 取消订单
   * POST /api/pubs/v1/recharge/cancel/{orderNo}
   */
  cancelRechargeOrder: (orderNo) => {
    return api.post(`/api/pubs/v1/recharge/cancel/${orderNo}`);
  },

  /**
   * 重新支付
   * POST /api/pubs/v1/recharge/repay/{orderNo}
   */
  repayRechargeOrder: (orderNo) => {
    return api.post(`/api/pubs/v1/recharge/repay/${orderNo}`);
  },

  // ==================== 消费记录相关 ====================

  /**
   * 获取用户消费记录（分页）
   * GET /api/pubs/v1/consumption/records
   */
  getConsumptionRecords: (params) => {
    return api.get('/api/pubs/v1/consumption/records', { params });
  },

  /**
   * 获取消费统计
   * GET /api/pubs/v1/consumption/statistics
   */
  getConsumptionStatistics: () => {
    return api.get('/api/pubs/v1/consumption/statistics');
  },

  /**
   * 按业务类型统计消费
   * GET /api/pubs/v1/consumption/by-type
   */
  getConsumptionByType: () => {
    return api.get('/api/pubs/v1/consumption/by-type');
  },

  // ==================== 余额查询相关 ====================

  /**
   * 获取用户余额
   * GET /api/pubs/v1/wallet/balance/{userId}
   */
  getBalance: (userId) => {
    return api.get(`/api/pubs/v1/wallet/balance/${userId}`);
  },
};

// 导出单独的函数供组件使用
export const {
  getWalletInfo,
  getWalletStatistics,
  getPointDetails,
  getExpiringSoonPoints,
  getRechargePackages,
  createRechargeOrder,
  getRechargeOrders,
  getRechargeOrderDetail,
  getRechargeOrderByNo,
  cancelRechargeOrder,
  repayRechargeOrder,
  getConsumptionRecords,
  getConsumptionStatistics,
  getConsumptionByType,
  getBalance,
} = walletService;

// 导出默认服务
export default walletService;
